import { deviceApi } from "#/api/deviceApi";
import { sitesApi } from "#/api/sitesApi";
import { showSuccessNotification } from "#/components/notifications";
import ArchivedBadge from "#/components/ui/ArchivedBadge";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  DeviceMobilityType,
  type DeviceUpdatePayload,
  StatusProcessingType,
} from "#/types/device";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import {
  Accordion,
  Button,
  Drawer,
  MultiSelect,
  Select,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEye } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import DeviceBasicInfoCard from "../devices/components/DeviceBasicInfoCard";
import HistoricalDataTable from "./components/HistoricalDataTable";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { useTranslation } from "react-i18next";
import { buildFixedStatusOptions } from "../devices/utils/fixedStatusOptions";

const DeviceDetailPage = () => {
  const { id } = useParams();
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const { t } = useTranslation();

  const [
    showHistoricalDrawer,
    { open: openHistoricalDrawer, close: closeHistoricalDrawer },
  ] = useDisclosure(false);

  const form = useForm<DeviceUpdatePayload>({
    initialValues: {
      name: "",
      site_id: undefined,
      mobility_type: undefined,
      status_processing_type: undefined,
      handler_name: "",
      fixed_status: undefined,
      selected_statuses: allowedStatuses,
      scan_threshold_in_seconds: 60,
    },
    validate: {
      name: (value) => (value ? null : t("devices.validation.nameRequired")),
      site_id: (value) => (value ? null : t("devices.validation.siteRequired")),
      mobility_type: (value) => (value ? null : t("devices.validation.mobilityTypeRequired")),
      status_processing_type: (value) =>
        value ? null : t("devices.validation.statusProcessingTypeRequired"),
      selected_statuses: (value, values) => {
        if (values.status_processing_type === StatusProcessingType.PRODUCT_STATUS_LOOP) {
          return value && value.length > 0 ? null : t("devices.validation.atLeastOneStatus");
        }
        return null;
      },
      fixed_status: (value, values) => {
        if (values.status_processing_type === StatusProcessingType.SINGLE_FIXED) {
          return value && value.trim() !== "" ? null : t("devices.validation.fixedStatusRequired");
        }
        return null;
      },
    },
  });

  // Fetch device details
  const deviceQuery = useQuery({
    queryKey: ["device-details", id],
    enabled: id !== undefined && id !== "new",
    queryFn: async () => {
      const response = await deviceApi.getById(Number(id));
      const device = response.data;

      // Populate form with device data
      form.setValues({
        name: device.name ?? "",
        site_id: device.site?.id,
        mobility_type: device.mobility_type,
        status_processing_type: device.status_processing_type,
        handler_name: device.handler_name,
        fixed_status: device.fixed_status,
        selected_statuses: device.selected_statuses && device.selected_statuses.length > 0 ? device.selected_statuses : allowedStatuses,
        scan_threshold_in_seconds: device.scan_threshold_in_seconds ?? 60,
      });
      form.resetDirty();

      return device;
    },
    staleTime: 1000 * 60,
  });

  // Fetch sites for dropdown
  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    staleTime: 1000 * 60,
  });

  // Update mutation
  const deviceMutation = useMutation({
    mutationFn: async (payload: DeviceUpdatePayload) => {
      if (id !== "new") {
        return await deviceApi.update(Number(id), payload);
      }
      throw new Error("Creating new devices not supported yet");
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("devices.updatedSuccess"),
      });
      void deviceQuery.refetch();
      form.resetDirty();
    },
    onError: (error) => {
      logger.error("Failed to update device:", error);
    },
  });

  const device = deviceQuery.data;
  const isLoading = deviceQuery.isPending;
  const isSaving = deviceMutation.isPending;
  const isArchived = device?.archived === true;
  const isReadOnly = isArchived || !canWrite();

  return (
    <DetailPageLayout
      title={
        <div className="flex items-center gap-2">
          <span>
            {id !== "new" ? t("devices.deviceDetails") : t("devices.createDevice")}
          </span>
          {isArchived && <ArchivedBadge />}
        </div>
      }
      loading={isLoading}
      navRightSection={
        id !== "new" && (
          <Button
            variant="outline"
            color="violet"
            leftSection={<IconEye size={16} />}
            onClick={openHistoricalDrawer}
            size="xs"
          >
            {t("common.historicalData")}
          </Button>
        )
      }
    >
      <div className="w-full flex flex-col gap-4">
        <form className="space-y-8 relative w-full">
          <Accordion
            variant="separated"
            defaultValue={["device_info", "basic_details"]}
            multiple
          >
            {device && (
              <DeviceBasicInfoCard device={device} detailed collapsible />
            )}
            <Accordion.Item value="basic_details">
              <Accordion.Control>
                <div className="flex items-center justify-between w-full pr-4">
                  <span>{t("common.basicDetails")}</span>
                  <div className="flex gap-2">
                    <Button
                      color="black"
                      variant="outline"
                      disabled={!form.isDirty() || isArchived}
                      onClick={(e) => {
                        e.stopPropagation();
                        form.reset();
                      }}
                      size="xs"
                    >
                      {t("common.clear")}
                    </Button>
                    <PermissionTooltip disabled={!canWrite()}>
                      <Button
                        color="black"
                        variant="filled"
                        loading={isSaving}
                        disabled={!form.isDirty() || !canWrite() || isArchived}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Through the form so the required-field rules run; a
                          // scanner must never be saved in a state it cannot operate in.
                          form.onSubmit((values) => deviceMutation.mutate(values))();
                        }}
                        size="xs"
                      >
                        {t("common.save")}
                      </Button>
                    </PermissionTooltip>
                  </div>
                </div>
              </Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-2 gap-4">
                  <TextInput
                    label={t("devices.form.name")}
                    withAsterisk
                    placeholder={t("devices.form.namePlaceholder")}
                    disabled={isReadOnly || isLoading || isSaving}
                    {...form.getInputProps("name")}
                  />

                  <TextInput
                    label={t("devices.form.handlerName")}
                    placeholder={t("devices.form.handlerNamePlaceholder")}
                    disabled={isReadOnly || isLoading || isSaving}
                    {...form.getInputProps("handler_name")}
                  />

                  <Select
                    label={t("devices.form.site")}
                    withAsterisk
                    placeholder={t("devices.form.sitePlaceholder")}
                    searchable
                    disabled={isReadOnly || isLoading || isSaving}
                    data={sites.map((prod) => {
                      return {
                        label: prod?.name ?? "",
                        value: prod?.id?.toString() ?? "",
                      };
                    })}
                    value={form.values.site_id?.toString()}
                    error={form.errors.site_id}
                    onChange={(_value) => {
                      form.setFieldValue("site_id", Number(_value));
                    }}
                  />

                  <Select
                    label={t("devices.form.mobilityType")}
                    withAsterisk
                    placeholder={t("devices.form.mobilityTypePlaceholder")}
                    searchable
                    disabled={isReadOnly || isLoading || isSaving}
                    data={Object.values(DeviceMobilityType).map((status) => ({
                      label: sanitize(status),
                      value: status,
                    }))}
                    clearable
                    value={form.values.mobility_type}
                    error={form.errors.mobility_type}
                    onChange={(_value) => {
                      form.setFieldValue(
                        "mobility_type",
                        _value as DeviceMobilityType
                      );
                    }}
                  />

                  <Select
                    label={t("devices.form.statusProcessingType")}
                    withAsterisk
                    placeholder={t("devices.form.statusProcessingTypePlaceholder")}
                    searchable
                    disabled={isReadOnly || isLoading || isSaving}
                    data={Object.values(StatusProcessingType).map((status) => ({
                      label: sanitize(status),
                      value: status,
                    }))}
                    clearable
                    value={form.values.status_processing_type}
                    error={form.errors.status_processing_type}
                    onChange={(_value) => {
                      form.setFieldValue(
                        "status_processing_type",
                        _value as StatusProcessingType
                      );
                    }}
                  />

                  {form.values.status_processing_type ===
                    StatusProcessingType.PRODUCT_STATUS_LOOP && (
                    <TextInput
                      label={t("devices.form.scanningThreshold")}
                      placeholder={t("devices.form.scanningThresholdPlaceholder")}
                      description={t("devices.form.scanningThresholdDescription")}
                      disabled={isReadOnly || isLoading || isSaving}
                      {...form.getInputProps("scan_threshold_in_seconds")}
                    />
                  )}

                  {form.values.status_processing_type ===
                    StatusProcessingType.PRODUCT_STATUS_LOOP && (
                    <div className="col-span-2">
                      <MultiSelect
                        label={t("devices.form.selectedStatuses")}
                        withAsterisk
                        placeholder={t("devices.form.selectedStatusesPlaceholder")}
                        description={t("devices.form.selectedStatusesDescription")}
                        searchable
                        disabled={isReadOnly || isLoading || isSaving}
                        data={allowedStatuses.map((status) => ({
                          label: sanitizeItemStatusLabel(status),
                          value: status,
                        }))}
                        value={form.values.selected_statuses || []}
                        error={form.errors.selected_statuses}
                        onChange={(value) => form.setFieldValue("selected_statuses", value)}
                        hidePickedOptions={false}
                        styles={{
                          pill: { maxWidth: "fit-content" },
                          input: { minHeight: "auto", height: "auto" },
                          inputField: { flexWrap: "wrap" }
                        }}
                      />
                    </div>
                  )}

                  {form.values.status_processing_type ===
                    StatusProcessingType.SINGLE_FIXED && (
                    <Select
                      label={t("devices.form.processingStatus")}
                      withAsterisk
                      placeholder={t("devices.form.statusProcessingTypePlaceholder")}
                      searchable
                      disabled={isReadOnly || isLoading || isSaving}
                      data={buildFixedStatusOptions(
                        allowedStatuses,
                        form.values.fixed_status,
                        t("devices.form.statusNotEnabled"),
                      )}
                      clearable
                      value={form.values.fixed_status}
                      error={form.errors.fixed_status}
                      onChange={(_value) => {
                        form.setFieldValue("fixed_status", _value as string);
                      }}
                    />
                  )}
                </div>
              </Accordion.Panel>
            </Accordion.Item>

          </Accordion>

          <Drawer
            opened={showHistoricalDrawer}
            onClose={closeHistoricalDrawer}
            position="right"
            size="90%"
            withCloseButton={false}
          >
            {/* Fixed viewport height (minus the drawer body padding) so the
                table pane inside gets a bounded box and scrolls on its own. */}
            <div className="flex h-[calc(100dvh-2rem)] w-full flex-col">
              <HistoricalDataTable
                device={device}
                onClose={closeHistoricalDrawer}
                headerStart={<Title order={3}>{t("common.historicalData")}</Title>}
              />
            </div>
          </Drawer>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default DeviceDetailPage;
